export interface ItemPrescricaoKit {
  id?: string;
  nome?: string;
  posologia: string;
  quantidade?: number;
  unit?: string;
  fabricante?: string;
  titularidade?: string;
}

export type LinhaNegocioMedikei = 'TELEMEDICINA' | 'HOME_CARE' | 'CORPORATIVO_B2B';

export const CLINICAL_KITS = {
  DENGUE: {
    key: 'DENGUE',
    nome: 'Kit Dengue / Sintomáticos Leves',
    descricao: 'Hidratação e analgesia segura para casos suspeitos de arboviroses (sem AINEs/AAS).',
    itens: [
      {
        nome: 'Dipirona Monoidratada 500 mg Comprimido',
        posologia:
          '<p>Tomar 1 a 2 comprimidos (500mg a 1g) por via oral de 6 em 6 horas se dor corporal ou febre (>37.8°C). <strong>Não utilizar Anti-inflamatórios ou AAS.</strong></p>',
        quantidade: 1,
        unit: 'caixa',
      },
      {
        nome: 'Sais para Reidratação Oral (SRO) Pó',
        posologia:
          '<p>Diluir 1 envelope em 1 litro de água filtrada. Ingerir 2 a 3 litros ao dia em pequenos goles.</p>',
        quantidade: 4,
        unit: 'envelopes',
      },
    ],
  },
  IVAS: {
    key: 'IVAS',
    nome: 'Kit IVAS / Resfriado & Gripe',
    descricao: 'Alívio sintomático de tosse, congestão e febre em infecções virais agudas.',
    itens: [
      {
        nome: 'Dipirona Monoidratada 1g Comprimido Efervescente',
        posologia: '<p>Dissolver 1 comprimido em meio copo de água a cada 6 ou 8 horas se dor ou febre.</p>',
        quantidade: 1,
        unit: 'tubo',
      },
      {
        nome: 'Cloreto de Sódio 0,9% Solução Nasal Spray',
        posologia:
          '<p>Aplicar 1 a 2 jatos em cada narina de 4 a 6 vezes ao dia para lavagem nasal abundante.</p>',
        quantidade: 1,
        unit: 'frasco',
      },
      {
        nome: 'Dropropizina 3 mg/mL Xarope',
        posologia: '<p>Tomar 10 mL por via oral de 8 em 8 horas se tosse seca irritativa (por até 5 dias).</p>',
        quantidade: 1,
        unit: 'frasco',
      },
    ],
  },
  HOME_CARE_POS_OP: {
    key: 'HOME_CARE_POS_OP',
    nome: 'Kit Pós-Operatório & Curativo Domiciliar',
    descricao: 'Analgesia escalonada, antiemético e cuidados pós-cirúrgicos domiciliares.',
    itens: [
      {
        nome: 'Paracetamol 750 mg Comprimido',
        posologia: '<p>Tomar 1 comprimido por via oral a cada 8 horas se dor moderada a intensa.</p>',
        quantidade: 1,
        unit: 'caixa',
      },
      {
        nome: 'Ondansetrona 8 mg Comprimido Orodispersível',
        posologia: '<p>Dissolver 1 comprimido sob a língua de 8 em 8 horas se náuseas ou vômitos.</p>',
        quantidade: 1,
        unit: 'caixa',
      },
    ],
  },
};

/**
 * Injeta em lote os itens de um Kit Clínico pré-montado no canvas da MeMed
 */
export async function aplicarKitClinicoMemed(itens: ItemPrescricaoKit[]) {
  if (typeof window === 'undefined' || !window.MdHub?.command?.send) {
    throw new Error('MdHub não disponível para injeção de kits.');
  }

  for (const item of itens) {
    if (item.id) {
      await window.MdHub.command.send('plataforma.prescricao', 'addItem', {
        id: item.id,
        posologia: item.posologia,
        quantidade: item.quantidade ?? 1,
        unit: item.unit ?? 'embalagem',
      });
    } else {
      await window.MdHub.command.send('plataforma.prescricao', 'addItem', {
        nome: item.nome,
        posologia: item.posologia,
        quantidade: item.quantidade ?? 1,
        fabricante: item.fabricante,
      });
    }
  }
}

/**
 * Reemissão Instantânea de Receitas anteriores utilizando o comando viewPrescription
 */
export async function repetirPrescricaoAnterior(idPrescricaoMemed: string | number) {
  if (typeof window === 'undefined' || !window.MdHub?.command?.send) {
    throw new Error('MdHub não disponível para reemissão.');
  }

  await window.MdHub.command.send(
    'plataforma.prescricao',
    'viewPrescription',
    String(idPrescricaoMemed),
  );
}

/**
 * Multi-Layout Dinâmico: Altera o papel timbrado ativo na MeMed conforme a modalidade
 * (1: Telemedicina, 2: Home Care, 3: B2B Corporativo)
 */
export async function aplicarLayoutReceituario(tipo: LinhaNegocioMedikei) {
  if (typeof window === 'undefined' || !window.MdHub?.command?.send) {
    return;
  }

  let indiceTema = 1;
  switch (tipo) {
    case 'TELEMEDICINA':
      indiceTema = 1;
      break;
    case 'HOME_CARE':
      indiceTema = 2;
      break;
    case 'CORPORATIVO_B2B':
      indiceTema = 3;
      break;
  }

  try {
    await window.MdHub.command.send('plataforma.sdk', 'find', {
      resource: `opcoes-receituario/ativar/${indiceTema}`,
      cache: false,
    });
  } catch (err) {
    console.warn('[aplicarLayoutReceituario] Falha ao ativar tema:', err);
  }
}

/**
 * Disclaimer Regulatório CFM de Telemedicina (Resolução CFM nº 2.314/2022 e MP 2.200-2/2001)
 */
export async function injetarMetadadosTelemedicina(dadosAtendimento: {
  numeroAtendimento: string;
  dataHora?: string;
}) {
  if (typeof window === 'undefined' || !window.MdHub?.command?.send) {
    return;
  }

  try {
    await window.MdHub.command.send('plataforma.prescricao', 'setAdditionalData', {
      header: [
        { Atendimento: `Teleconsulta nº ${dadosAtendimento.numeroAtendimento}` },
        { Regulamentação: 'Resolução CFM nº 2.314/2022' },
        { Canal: 'Medikei Saúde - Plataforma Oficial' },
      ],
      footer:
        'Prescrição eletrônica emitida em ambiente seguro de Telemedicina.\n' +
        'Documento assinado digitalmente nos padrões ICP-Brasil com validade jurídica nacional.\n' +
        'Validação de autenticidade acessível via QR Code impresso no documento.',
    });
  } catch (err) {
    console.warn('[injetarMetadadosTelemedicina] Falha ao enviar additionalData:', err);
  }
}

export interface DocumentoGerado {
  id?: number | string;
  uuid?: string;
  type?: 'full' | 'individual' | 'signature' | string;
  file_name?: string;
  status?: string;
  prescription_uuid?: string;
  [key: string]: unknown;
}

/**
 * Segregação de Vias para Privacidade LGPD:
 * Separa o PDF consolidado do prontuário das vias individuais disponibilizadas
 * ao paciente (evitando que atestados de trabalho exponham medicações).
 */
export function segregarDocumentosParaPaciente(
  documents: DocumentoGerado[] = [],
  itensMedicamentos: any[] = [],
) {
  const temAtestado = itensMedicamentos.some(
    (m) => (m.tipo === 'custom' && m.custom_categoria === 'atestado') || m.custom_categoria === 'atestado',
  );

  return {
    documentoCompletoProntuario: documents.find((doc) => doc.type === 'full') || documents[0] || null,
    documentosDisponiveisPaciente: documents
      .filter((doc) => doc.type === 'individual')
      .map((doc, index) => ({
        uuid: doc.uuid,
        nomeExibicao: index === 0 && temAtestado ? 'Atestado Médico Oficial' : `Receituário Via ${index + 1}`,
        tipo: doc.type,
      })),
  };
}
